<template>
    <div
        :class="classTextWithImage"
        class="text-with-image"
    >
        <div
            v-if="data.image"
            class="image"
        >
            <CmsImage
                :data="data.image"
                fit="cover"
                :is-eager="hasEagerImage"
                :sizes="['767px', [768, '720px']]"
            />
        </div>

        <div class="text-content">
            <ContentComponentItem
                v-for="(component, key) in data.content"
                :key="key"
                :background="background"
                :data="component"
                :page-type="pageType"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Color, ComponentTextWithImageFragment } from '@/graphql/api'

import ContentComponentItem from '@/components/cms/component/ContentComponentItem.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const props = defineProps<{
    data: ComponentTextWithImageFragment
    background: Color
    hasEagerImage?: boolean
    pageType: PageType
}>()

const classTextWithImage = computed(() => ({
    [`padding-${props.data.padding.toLocaleLowerCase()}`]: true,
    [`${props.data.imageFormat.toLocaleLowerCase()}-image`]: true,
    [`${props.data.imagePosition.toLocaleLowerCase()}-image`]: true,
}))
</script>

<style lang="scss" scoped>
.text-with-image {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;

    @include min-width(768) {
        display: flex;
        align-items: center;
    }

    &.right-image {
        flex-direction: row-reverse;
    }

    &.padding-none {
        .image {
            padding: 0;
        }
    }

    &.padding-small {
        .image {
            @include min-width(768) {
                padding: 20px;
            }
        }
    }

    &.padding-medium {
        .image {
            @include min-width(768) {
                padding: 40px;
            }
        }
    }

    &.padding-large {
        .image {
            @include min-width(768) {
                padding: 60px;
            }
        }
    }

    &.rounded-image {
        .image {
            width: 280px;
            height: 420px;
            border-radius: 140px;
            max-width: 100%;
            overflow: hidden;
            margin: 0 auto;
            flex-shrink: 0;

            @include min-width(360) {
                width: 335px;
                height: 500px;
                border-radius: 200px;
            }

            @include min-width(768) {
                margin: 0 0 0 40px;
            }

            @include min-width(1024) {
                margin: 0 0 0 90px;
            }

            @include min-width(1140) {
                margin: 0 0 0 180px;
            }
        }

        &.right-image {
            .image {
                margin: 0 auto;

                @include min-width(768) {
                    margin: 0 40px 0 0;
                }

                @include min-width(1024) {
                    margin: 0 90px 0 0;
                }

                @include min-width(1140) {
                    margin: 0 180px 0 0;
                }
            }
        }

        &.padding-small,
        &.padding-medium,
        &.padding-large {
            .image {
                @include min-width(768) {
                    padding: 0;
                }
            }
        }
    }

    &.portrait-image {
        .image {
            aspect-ratio: 1/1;

            @include min-width(768) {
                max-width: 40%;
                aspect-ratio: 3/4;
            }
        }
    }

    &.landscape-image {
        .image {
            aspect-ratio: 4/3;
        }
    }

    .image {
        width: 100%;
        aspect-ratio: 1/1;
        line-height: 0;

        @include min-width(768) {
            width: 100%;
            max-width: 50%;
            align-self: stretch;
        }
    }

    .text-content {
        width: 100%;
        margin: 0 auto;
        padding: 40px 20px 10px;

        @include min-width(768) {
            max-width: 50%;
        }

        @include min-width(1024) {
            max-width: 510px;
        }
    }
}
</style>
